import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, inject } from '@angular/core';
import { Dialog } from 'primeng/dialog';
import { NgClass, NgIf } from '@angular/common';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { DateTime } from 'luxon';
import { NgLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from '@ng-select/ng-select';
import { AuditReportUserSelectComponent } from '../audit-report-user-select/audit-report-user-select.component';
import { Observable, Subscription } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { AuditReportUserDto } from '../../common/interfaces/audit-report-user-dto';
import { AuditReportService } from '../../common/services/audit-report.service';
import { AuditReportDto } from '../../common/interfaces/audit-report-dto';
import { MmDatepickerComponent } from '../mm-datepicker/mm-datepicker.component';
import { EnterpriseLabels } from '../../common/interfaces/enterprise-labels';
import { AdminAuditEventCategory } from '../../common/enums/admin-audit-event-category.enum';
import { remoteValueExistsValidator } from '../../common/validators/remote-value-exists/remote-value-exists.validator';
import { dateMinMaxValidator } from '../../common/validators/date-min-max/date-min-max.validator';
import { dateNotBeforeFormControlDateValidator } from '../../common/validators/date-not-before/date-not-before.validator';
import { getEventsByCategory } from '../../common/utils/business-history-category-util';
import { AdminAuditEventType } from '../../common/enums/admin-audit-event-type.enum';
import { SsoUserDetailsDto } from '../../common/interfaces/sso-user-details-dto';
import { AuditReportStatus } from '../../common/enums/audit-report-status';
import { EnterpriseDto } from '../../common/interfaces/enterprise-dto';
import { AuditReportEnterpriseSelectComponent } from '../audit-report-enterprise-select/audit-report-enterprise-select.component';
import { envToken } from '../../common/tokens/env-token';

const namePattern = '^[-A-Za-zÀ-ÖØ-öø-ÿ0-9_@.\'"]+$';
const dateFormat = 'MM/dd/yyyy';
const minDateLimit = '01/01/2024';

@Component({
    selector: 'lib-audit-report-request-modal',
    imports: [
        Dialog,
        MmDatepickerComponent,
        NgIf,
        ReactiveFormsModule,
        NgClass,
        NgLabelTemplateDirective,
        NgOptionTemplateDirective,
        NgSelectComponent,
        FormsModule,
        AuditReportUserSelectComponent,
        AuditReportEnterpriseSelectComponent
    ],
    templateUrl: './audit-report-request-modal.component.html',
    styleUrl: './audit-report-request-modal.component.scss',
    standalone: true
})
export class AuditReportRequestModalComponent implements OnInit, OnDestroy {
    private formBuilder = inject(FormBuilder);
    private readonly auditReportService = inject(AuditReportService);
    private toastrService = inject(ToastrService);
    private env = inject(envToken);

    private SOURCE_SYSTEM_ENTERPRISE_ADMIN = 'ENTERPRISE_ADMIN';

    @Input() enterpriseLabels!: EnterpriseLabels;
    @Input() showModal = false;
    @Input() enterpriseId!: number;
    @Output() isRequestingAuditReport = new EventEmitter<boolean>();

    dateMin = DateTime.fromFormat(minDateLimit, dateFormat).toJSDate();
    dateMax = DateTime.now().toJSDate();
    eventCategories: { value: string; label: string }[] = [];
    eventNames: { value: string; label: string }[] = [];
    subscriptions = new Subscription();
    rangeDatesPeriod = 90;
    isValidatingReportName = false;
    users: AuditReportUserDto[] = [];
    enterprises: EnterpriseDto[] = [];
    sourceSystem = this.SOURCE_SYSTEM_ENTERPRISE_ADMIN;

    form: FormGroup = new FormGroup({
        reportName: new FormControl(''),
        fromDate: new FormControl(false),
        toDate: new FormControl(false),
        eventCategory: new FormControl(''),
        eventName: new FormControl('')
    });

    ngOnInit(): void {
        this.sourceSystem = this.env.sourceSystem;
        this.eventCategories = Object.entries(AdminAuditEventCategory)
            .map(([key, value]) => ({
                value: key,
                label: value.replace('Universal ID', this.enterpriseLabels.universalIdLabel)
                    .replace('EMA Account', this.enterpriseLabels.accountLabel)
            }));
        this.updateEventNames();
        this.form = this.formBuilder.group({
            reportName: ['',
                {
                    validators: [Validators.required, Validators.maxLength(255), Validators.pattern(namePattern)],
                    asyncValidators: [
                        remoteValueExistsValidator(
                            this.isReportNameUnique.bind(this),
                            false,
                            () => this.isValidatingReportName = true, () => this.isValidatingReportName = false
                        )
                    ]
                }
            ],
            fromDate: ['', [Validators.required, dateMinMaxValidator(minDateLimit, DateTime.now().toFormat(dateFormat))]],
            toDate: ['', [Validators.required, dateMinMaxValidator(minDateLimit, DateTime.now().toFormat(dateFormat)),
                dateNotBeforeFormControlDateValidator('fromDate', this.rangeDatesPeriod)]],
            eventCategory: [''],
            eventName: ['']
        });

        this.subscriptions.add(this.form.get('fromDate')?.valueChanges.subscribe(() => {
            this.form.get('toDate')?.updateValueAndValidity();
        }));
    }

    ngOnDestroy() {
        this.subscriptions.unsubscribe();
    }

    closeModal(): void {
        this.isRequestingAuditReport.emit(false);
    }

    sendRequest(): void {
        if (this.form.invalid) {
            return;
        }
        const auditReportDto: Partial<AuditReportDto> = this.createAuditReportDto();
        this.auditReportService.sendRequest(auditReportDto).subscribe({
            next: () => {
                this.toastrService.success('The request was successfully created', 'Audit Report Request');
                this.closeModal();
            },
            error: () => {
                this.toastrService.error('Failed to create request', 'Audit Report Request');
            }
        });
    }

    updateEventNames(): void {
        const eventsByCategory = this.eventCategory ? getEventsByCategory(this.eventCategory) : [];
        this.eventNames = Object.entries(AdminAuditEventType)
            .filter(([key]) => {
                return this.eventCategory ? eventsByCategory.includes(key) : true;
            })
            .map(([key, value]) => ({
                value: key,
                label: value.replace('Universal ID', this.enterpriseLabels.universalIdLabel)
            }));
    }

    onEventCategoryChange(value: string): void {
        this.resetEventNameValue(value);
        this.updateEventNames();
    }

    resetEventNameValue(value: string): void {
        const control = this.form.get('eventName');
        if (value && control) {
            control.patchValue('');
            control.markAsDirty();
            control.updateValueAndValidity();
        }
    }

    preventClickPropagation(event: Event) {
        event.stopImmediatePropagation();
    }

    get eventCategory() {
        return this.form.get('eventCategory')?.value;
    }

    get eventName() {
        return this.form.get('eventName')?.value;
    }

    isReportNameUnique(reportName: string): Observable<boolean> {
        const reportEnterpriseId = this.isOrgAdminApp() ? this.enterpriseId : this.groupAdminAppSelectedEnterpriseId();
        return this.auditReportService.isReportNameUnique(this.sourceSystem, reportEnterpriseId, reportName);
    }

    onUserSelected(selected: (SsoUserDetailsDto[] | SsoUserDetailsDto | null | undefined)): void {
        this.users = (selected ? (Array.isArray(selected) ? selected : [selected]) : [])
            .map(user => ({ user: { id: user.id } } as AuditReportUserDto));
    }

    onEnterpriseSelected(selected: (EnterpriseDto[] | EnterpriseDto | null | undefined)): void {
        this.enterprises = (selected ? (Array.isArray(selected) ? selected : [selected]) : [])
            .map(ent => ({ id: ent.id, name: ent.name } as EnterpriseDto));
        this.form.get('reportName')?.updateValueAndValidity({ onlySelf: true, emitEvent: false });
    }

    groupAdminAppSelectedEnterpriseId(): number {
        return (this.enterprises?.length ? this.enterprises[ 0 ].id : 0) ?? 0;
    }

    private createAuditReportDto(): Partial<AuditReportDto> {
        const reportName = this.form.get('reportName')?.value?.trim();
        const auditReportDto: Partial<AuditReportDto> =
            {
                enterpriseId: this.isOrgAdminApp() ? this.enterpriseId : this.groupAdminAppSelectedEnterpriseId(),
                reportName: reportName,
                fileName: `${reportName.replace(/\s/g, '')}.csv`,
                status: AuditReportStatus.PENDING,
                fromDate: this.getDateTime('fromDate'),
                toDate: this.getDateTime('toDate', true),
                users: this.users,
                ...(this.eventCategory && { eventCategory: this.eventCategory }),
                ...(this.eventName && { eventName: this.eventName }),
                groupAdmin: this.isOrgAdminApp() ? 0 : 1
            };

        return auditReportDto;
    }

    getDateTime(formCtr: string, endOfDay = false): Date | null {
        const dt = this.form.get(formCtr)?.value;
        return dt ? new Date(dt.getFullYear(), dt.getMonth(), dt.getDate(),
                             endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0, endOfDay ? 999 : 0
        ) : null;
    }

    isOrgAdminApp(): boolean {
        return this.sourceSystem === this.SOURCE_SYSTEM_ENTERPRISE_ADMIN;
    }
}
